"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { loadFromLocalStorage, saveToLocalStorage } from "@/lib/utils/persistence";

interface LayoutContextType {
  layout: string;
  compactMode: boolean;
  animations: boolean;
  highContrast: boolean;
  setLayout: (layout: string) => void;
  setCompactMode: (enabled: boolean) => void;
  setAnimations: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export function LayoutProvider({ children }: { children: React.ReactNode }) {
  const [layout, setLayout] = useState(() => loadFromLocalStorage("layout", "default"));
  const [compactMode, setCompactMode] = useState(() => loadFromLocalStorage("compactMode", false));
  const [animations, setAnimations] = useState(() => loadFromLocalStorage("animations", true));
  const [highContrast, setHighContrast] = useState(() => loadFromLocalStorage("highContrast", false));

  // Save to localStorage whenever any setting changes
  useEffect(() => {
    saveToLocalStorage("layout", layout);
  }, [layout]);

  useEffect(() => {
    saveToLocalStorage("compactMode", compactMode);
  }, [compactMode]);

  useEffect(() => {
    saveToLocalStorage("animations", animations);
  }, [animations]);

  useEffect(() => {
    saveToLocalStorage("highContrast", highContrast);
  }, [highContrast]);

  // Apply layout classes
  useEffect(() => {
    document.documentElement.classList.toggle("compact-mode", compactMode);
    document.documentElement.classList.toggle("layout-spacious", layout === "spacious");
    document.documentElement.classList.toggle("layout-compact", layout === "compact");
    document.documentElement.classList.toggle("animations-enabled", animations);
    document.documentElement.classList.toggle("high-contrast", highContrast);
  }, [layout, compactMode, animations, highContrast]);

  return (
    <LayoutContext.Provider
      value={{
        layout,
        compactMode,
        animations,
        highContrast,
        setLayout,
        setCompactMode,
        setAnimations,
        setHighContrast,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
} 